import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PageHero from "../components/PageHero";
import { API } from "../config/api";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Receipt, Loader2, Clock, ChevronRight, Search,
  FileText, Send, UserCheck, PoundSterling, AlertTriangle,
  RefreshCw, Plus, ShieldAlert, ArrowUpRight, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { getUserRole, ROLES } from "../config/roleConfig";
import AllocateFeeEarnerButton from "../components/AllocateFeeEarnerButton";
import { useSmartSearchResolver } from "../hooks/useSmartSearchResolver";
import { errorMessage } from "../utils/errorMessage";

// ─── shared helpers ───────────────────────────────────────────────────────────

function authHeaders() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email =
    user.user_email || user.email || user.ms_email ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id":
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

function fmtGbp(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  if (typeof raw === "string" && /[£$€]/.test(raw)) return raw.trim();
  const n = Number(typeof raw === "string" ? raw.replace(/[,\s]/g, "") : raw);
  if (!Number.isFinite(n)) return typeof raw === "string" ? raw : "";
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

// Normalise a matter reference for joining the two datasets.
function normRef(ref) {
  return (ref || "").trim().toUpperCase();
}

// ─── Pipeline (pre-issue) status logic ───────────────────────────────────────

const PIPELINE_BADGES = {
  "": { bg: "bg-slate-500/15", text: "text-slate-300", label: "Draft Needed" },
  draft_uploaded: { bg: "bg-blue-500/15", text: "text-blue-300", label: "Draft Uploaded" },
  notified: { bg: "bg-status-amber/15", text: "text-status-amber", label: "Awaiting Approval" },
  approved: { bg: "bg-teal/15", text: "text-teal", label: "Approved" },
  returned: { bg: "bg-status-red/15", text: "text-status-red", label: "Returned" },
  sent_to_client: { bg: "bg-teal/15", text: "text-teal", label: "Sent to Client" },
  cancelled: { bg: "bg-slate-700/30", text: "text-slate-400", label: "Cancelled" },
};

function getPipelineStatus(item) {
  if (!item) return null;
  if (item.approval_status === "cancelled") return "cancelled";
  if (item.sent_to_client_at) return "sent_to_client";
  if (item.approval_status === "approved") return "approved";
  if (item.approval_status === "returned") return "returned";
  if (item.fee_earner_notified_at) return "notified";
  if (item.draft_uploaded) return "draft_uploaded";
  return "";
}

// ─── Credit Control (post-issue) status logic ────────────────────────────────

const CC_STAGE_COLORS = {
  invoice_issued: { bg: "bg-teal/20", text: "text-teal", label: "Invoice Issued" },
  reminder_1: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Reminder 1" },
  call_1: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Call 1" },
  reminder_2: { bg: "bg-status-amber/20", text: "text-status-amber", label: "Reminder 2" },
  call_2: { bg: "bg-status-amber/20", text: "text-status-amber", label: "Call 2" },
  reminder_3: { bg: "bg-status-red/20", text: "text-status-red", label: "Reminder 3" },
  call_3: { bg: "bg-status-red/20", text: "text-status-red", label: "Call 3" },
  lba_sent: { bg: "bg-gray-700/60", text: "text-gray-300", label: "LBA Sent" },
  proceedings: { bg: "bg-gray-700/60", text: "text-gray-300", label: "Proceedings" },
};

const CC_STATUS_COLORS = {
  active: { bg: "bg-blue-500/20", text: "text-blue-400" },
  part_paid: { bg: "bg-status-amber/20", text: "text-status-amber" },
  paid: { bg: "bg-teal/20", text: "text-teal" },
  disputed: { bg: "bg-status-red/20", text: "text-status-red" },
  lba_sent: { bg: "bg-gray-600/40", text: "text-gray-300" },
  referred: { bg: "bg-purple-500/20", text: "text-purple-400" },
  written_off: { bg: "bg-gray-700/40", text: "text-gray-500" },
  payment_arrangement: { bg: "bg-teal/20", text: "text-teal" },
  pending_match: { bg: "bg-yellow-500/20", text: "text-yellow-400" },
};

// Stages that mean active recovery / chasing is under way.
const RECOVERY_STAGES = new Set([
  "reminder_3", "call_3", "lba_sent", "proceedings",
]);

function StageBadge({ stage }) {
  const c = CC_STAGE_COLORS[stage] || { bg: "bg-gray-600/40", text: "text-gray-400", label: stage };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>;
}

function StatusPill({ status }) {
  const c = CC_STATUS_COLORS[status] || { bg: "bg-gray-600/40", text: "text-gray-400" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      {status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
}

function PipelineBadge({ status }) {
  const b = PIPELINE_BADGES[status] || PIPELINE_BADGES[""];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${b.bg} ${b.text}`}>{b.label}</span>;
}

function OverdueBadge({ days }) {
  if (!days || days <= 0) return null;
  const cls = days <= 7 ? "text-teal" : days <= 14 ? "text-yellow-400" : days <= 21 ? "text-status-amber" : days <= 28 ? "text-status-red" : "text-gray-300 font-bold";
  return <span className={`text-xs font-medium ${cls}`}>{days}d overdue</span>;
}

// ─── Create CC record modal ──────────────────────────────────────────────────

function CreateCCModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ matter_ref: "", client_name: "", fee_earner: "", invoice_number: "", amount: "", due_date: "", invoice_date: "" });
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const handleCreate = async () => {
    if (!form.matter_ref || !form.client_name) return;
    setSaving(true);
    try {
      const invoices = form.invoice_number ? [{ invoice_number: form.invoice_number, amount_outstanding: parseFloat(form.amount) || 0, due_date: form.due_date }] : [];
      await axios.post(`${API}/credit-control/create`, { matter_ref: form.matter_ref, client_name: form.client_name, fee_earner: form.fee_earner, invoices, invoice_date: form.invoice_date, due_date: form.due_date }, { headers: authHeaders() });
      onCreated();
      onClose();
    } catch (err) {
      console.error("Create failed:", err);
      toast.error("Could not create credit control record");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-bold text-white mb-4">New Credit Control Record</h3>
        <div className="space-y-3">
          {[
            { key: "matter_ref", label: "Matter Reference", placeholder: "BL.VEC.006.0503" },
            { key: "client_name", label: "Client Name", placeholder: "Smith & Co" },
            { key: "fee_earner", label: "Fee Earner (initials)", placeholder: "VEC" },
            { key: "invoice_number", label: "Invoice Number", placeholder: "INV-2026-042" },
            { key: "amount", label: "Amount Outstanding (£)", placeholder: "1500.00", type: "number" },
            { key: "due_date", label: "Due Date", type: "date" },
            { key: "invoice_date", label: "Invoice Date", type: "date" },
          ].map(f => (
            <div key={f.key}>
              <label className="text-xs text-gray-400 block mb-1">{f.label}</label>
              <input type={f.type || "text"} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder}
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button onClick={handleCreate} disabled={saving || !form.matter_ref || !form.client_name} className="bg-teal text-black hover:bg-teal-hover">
            {saving ? "Creating..." : "Create Record"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Unlink invoice request from Credit Control ──────────────────────────────

function UnlinkCCModal({ pipeline, onClose, onUnlinked }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!pipeline) return null;

  const handleUnlink = async () => {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      setError("Please give a short reason (at least 3 characters).");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API}/credit-control/invoice-requests/${pipeline.id}/unlink-cc`,
        { reason: trimmedReason },
        { headers: authHeaders() },
      );
      toast.success("Credit control link removed");
      onUnlinked();
      onClose();
    } catch (err) {
      const message = errorMessage(err, "Could not remove the credit control link.");
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-2">Remove credit control link?</h3>
        <p className="text-sm text-gray-400 mb-4">
          This removes the credit-control association from this invoice request only. It will not affect the credit-control record itself.
        </p>
        <label className="text-xs text-gray-400 block mb-1" htmlFor="unlink-cc-reason">Reason</label>
        <textarea
          id="unlink-cc-reason"
          value={reason}
          onChange={e => { setReason(e.target.value); setError(""); }}
          placeholder="Why should this invoice request be unlinked?"
          rows={3}
          className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
        />
        {error && <p className="mt-2 text-xs text-status-red">{error}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-gray-400">Cancel</Button>
          <Button onClick={handleUnlink} disabled={saving} className="bg-status-red text-white hover:bg-status-red/80">
            {saving ? "Removing..." : "Unlink"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Unified lifecycle row ───────────────────────────────────────────────────
//
// One row per matter. Carries the pipeline (pre-issue) badge AND, once the
// matter has a credit-control record, the CC stage / outstanding / overdue plus
// a direct deep-link into that invoice's full credit-control record.

function LifecycleRow({ row, navigate, canWrite, onAllocated, onUnlinkRequested }) {
  const { pipeline, cc } = row;
  const pipelineStatus = getPipelineStatus(pipeline);
  const hasCC = !!cc;

  const matterRef = pipeline?.matter_reference || cc?.matter_ref || "No Reference";
  const invoiceRequestNumber = (
    pipeline?.data?.invoice_request_number ||
    pipeline?.invoice_request_number || ""
  ).trim();
  const clientName = pipeline?.client_name || cc?.client_name || "";
  const responsibleFE = (
    pipeline?.data?.responsible_fee_earner ||
    pipeline?.responsible_fee_earner ||
    cc?.fee_earner || ""
  ).trim();
  const needsAllocation = !!pipeline && !responsibleFE;

  // Total invoiced exc VAT — prefer the net fixed-fee figure; fall back to
  // the gross amount only when no net figure was captured (WIP-path invoices).
  const netRaw = pipeline?.amount_net || "";
  const totalInvoicedExcVat = netRaw ? fmtGbp(netRaw) : (pipeline ? fmtGbp(pipeline.amount) : "");
  const isNet = !!netRaw;

  // Date billed — the invoice date once issued (CC), else the date it was
  // sent to the client from the pipeline.
  const dateBilled = cc?.invoice_date ? fmtDate(cc.invoice_date) : (pipeline?.sent_to_client_at ? fmtDate(pipeline.sent_to_client_at) : "");

  const outstanding = hasCC ? (cc.total_outstanding || 0) : 0;
  const hasOutstanding = outstanding > 0;

  // A row click always opens the invoice page for the matter so it can keep
  // being worked on, whether or not a submission record exists yet. When a
  // submission exists the page is keyed on its id; otherwise it is keyed on the
  // matter reference the credit-control record carries. The row never falls back
  // to the credit-control record - that has its own discrete button below.
  const handleRowClick = () => {
    if (pipeline) navigate(`/submission/${pipeline.id}`);
    else if (hasCC) navigate(`/submission/${encodeURIComponent(cc.matter_ref)}`);
  };

  const accent = hasCC && RECOVERY_STAGES.has(cc.escalation_stage)
    ? "border-status-red/25 hover:border-status-red/45"
    : "border-teal/10 hover:border-teal/25";

  return (
    <div className={`w-full glass-card rounded-lg p-4 border ${accent} transition-colors`}>
      <button onClick={handleRowClick} className="w-full text-left">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1.5">
              <h4 className="font-bold text-white truncate">{matterRef}</h4>
              {invoiceRequestNumber && (
                <span
                  className="text-[11px] font-mono font-semibold tracking-wide text-teal bg-teal/10 border border-teal/30 rounded px-1.5 py-0.5"
                  data-testid="invoice-tracker-request-number"
                >
                  {invoiceRequestNumber}
                </span>
              )}
              {/* Pipeline badge — always shown while the matter is pre-issue,
                  and as a 'Sent to Client' marker once issued. */}
              {pipeline && <PipelineBadge status={pipelineStatus} />}
              {/* CC stage + status once the matter has been issued. */}
              {hasCC && <StageBadge stage={cc.escalation_stage} />}
              {hasCC && <StatusPill status={cc.status} />}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-txt-muted">
              {clientName && (
                <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {clientName}</span>
              )}
              {pipeline?.requester_name && (
                <span className="flex items-center gap-1"><Send className="w-3 h-3" /> {pipeline.requester_name}</span>
              )}
              {pipeline?.created_at && (
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(pipeline.created_at)}</span>
              )}
              {totalInvoicedExcVat && (
                <span className="flex items-center gap-1 font-medium text-teal">
                  <PoundSterling className="w-3 h-3" /> {totalInvoicedExcVat}{isNet ? " exc VAT" : ""}
                </span>
              )}
              {dateBilled && (
                <span className="flex items-center gap-1"><Receipt className="w-3 h-3" /> Billed {dateBilled}</span>
              )}
              {/* Outstanding-at-a-glance: green tick when clear, amber figure when owed. */}
              {hasCC && (
                hasOutstanding ? (
                  <span className="flex items-center gap-1 font-medium text-status-amber">
                    <PoundSterling className="w-3 h-3" /> {fmtGbp(outstanding)} outstanding
                  </span>
                ) : (
                  <span className="flex items-center gap-1 font-medium text-teal">
                    <CheckCircle2 className="w-3 h-3" /> Paid in full
                  </span>
                )
              )}
              {/* Overdue is only meaningful while a balance is owed — a
                  paid-in-full matter never shows an overdue badge. */}
              {hasCC && hasOutstanding && <OverdueBadge days={cc.days_overdue} />}
              {hasCC && cc.next_action_due && (
                <span className="flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Next: {cc.next_action_due}</span>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-txt-faint shrink-0 mt-1" />
        </div>
      </button>

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        {needsAllocation ? (
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
            <span className="text-xs text-status-amber">No fee earner allocated</span>
            <AllocateFeeEarnerButton submissionId={pipeline.id} onAllocated={onAllocated} buttonSize="sm" />
          </div>
        ) : responsibleFE ? (
          <div className="flex items-center gap-1.5 text-xs text-teal">
            <UserCheck className="w-3 h-3" />
            <span>FE: {responsibleFE}</span>
          </div>
        ) : <span />}

        {/* Direct deep-link into the full credit-control record — the detail
            that can't sensibly live on a tracker row. */}
        <div className="flex items-center gap-2">
          {hasCC && canWrite && pipeline?.id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onUnlinkRequested(pipeline); }}
              className="text-xs text-gray-400 hover:text-status-red"
              data-testid="unlink-cc-record"
            >
              Unlink
            </Button>
          )}
          {hasCC && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); navigate(`/credit-control/${cc.id}`); }}
              className="text-teal border-teal/30 hover:border-teal/50 hover:text-teal-hover"
              data-testid="open-cc-record"
            >
              Open credit control record
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Summary cards (filter the single list, no tabs) ──────────────────────────

function SummaryCards({ rows, ccSummary, activeFilter, onFilter }) {
  const draftNeeded = rows.filter(r => r.pipeline && getPipelineStatus(r.pipeline) === "").length;
  const awaitingApproval = rows.filter(r => r.pipeline && getPipelineStatus(r.pipeline) === "notified").length;
  const totalOutstanding = ccSummary.total_outstanding || 0;
  const preLba =
    (ccSummary.by_stage?.reminder_3 || 0) +
    (ccSummary.by_stage?.call_3 || 0) +
    (ccSummary.by_stage?.lba_sent || 0) +
    (ccSummary.by_stage?.proceedings || 0);

  const cards = [
    { key: "draft_needed", label: "Draft Needed", value: draftNeeded, valueClass: draftNeeded > 0 ? "text-status-amber" : "text-white", title: "Invoice requests waiting for a draft" },
    { key: "awaiting", label: "Awaiting Approval", value: awaitingApproval, valueClass: awaitingApproval > 0 ? "text-yellow-400" : "text-white", title: "Drafts sent to fee earner, pending approval" },
    { key: "outstanding", label: "Total Outstanding", value: `£${totalOutstanding.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`, valueClass: "text-teal", title: "Total value of all active credit-control invoices" },
    { key: "recovery", label: "Pre-LBA / Recovery", value: preLba, valueClass: preLba > 0 ? "text-status-red" : "text-white", title: "Matters at Reminder 3, Call 3, LBA or Proceedings" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {cards.map(c => (
        <button key={c.key} onClick={() => onFilter(activeFilter === c.key ? "" : c.key)} title={c.title}
          className={`bg-navy-card border rounded-lg p-4 text-left transition-colors ${
            activeFilter === c.key ? "border-teal/60" : "border-white/10 hover:border-white/20"
          }`}>
          <div className="text-xs text-gray-400 mb-1">{c.label}</div>
          <div className={`text-2xl font-bold ${c.valueClass}`}>{c.value}</div>
        </button>
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function InvoiceTracker() {
  const navigate = useNavigate();

  const [pipelineItems, setPipelineItems] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [pipelineError, setPipelineError] = useState("");

  const [ccRecords, setCcRecords] = useState([]);
  const [ccSummary, setCcSummary] = useState({ active_count: 0, total_outstanding: 0, by_stage: {} });
  const [ccLoading, setCcLoading] = useState(true);
  const [ccError, setCcError] = useState("");

  const [search, setSearch] = useState("");
  const [cardFilter, setCardFilter] = useState(""); // "" | draft_needed | awaiting | outstanding | recovery

  const userEmail = (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") || ""
  ).toLowerCase();
  const userRole = getUserRole(userEmail);
  const isMay = userRole === ROLES.ACCOUNTS || userRole === ROLES.DIRECTOR;
  const canWrite = isMay;

  const [showCreate, setShowCreate] = useState(false);
  const [unlinkingPipeline, setUnlinkingPipeline] = useState(null);

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    setPipelineError("");
    try {
      // Keep this request on the same explicit session-header path as the
      // credit-control requests below rather than depending solely on the
      // global interceptor side effect.
      const res = await axios.get(`${API}/submissions/invoice-tracker`, {
        headers: authHeaders(),
      });
      setPipelineItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const reason = errorMessage(err, "Failed to load invoice pipeline.");
      setPipelineError(reason);
      setPipelineItems([]);
      toast.error("Failed to load invoice pipeline");
    } finally {
      setPipelineLoading(false);
    }
  }, []);

  const fetchCC = useCallback(async () => {
    setCcLoading(true);
    setCcError("");
    try {
      const [listRes, summaryRes] = await Promise.all([
        axios.get(`${API}/credit-control/list`, { headers: authHeaders() }),
        axios.get(`${API}/credit-control/overdue-summary`, { headers: authHeaders() }),
      ]);
      setCcRecords(listRes.data.records || []);
      setCcSummary(summaryRes.data);
    } catch (err) {
      const reason = errorMessage(err, "Failed to load credit control.");
      setCcError(reason);
      setCcRecords([]);
    } finally {
      setCcLoading(false);
    }
  }, []);

  const fetchAll = useCallback(() => { fetchPipeline(); fetchCC(); }, [fetchPipeline, fetchCC]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build one lifecycle row per invoice request. A stored credit_control_id
  // takes precedence; the old matter-reference display join remains only for
  // requests that have never been explicitly linked or unlinked.
  const rows = useMemo(() => {
    const ccByRef = new Map();
    const ccById = new Map();
    const standaloneCCRows = [];

    for (const rec of ccRecords) {
      if (rec.id) ccById.set(String(rec.id), rec);
      const matterRef = normRef(rec.matter_ref);
      if (!matterRef) {
        // No matter ref - keep as its own standalone row so nothing is hidden.
        standaloneCCRows.push({ key: `__cc__${rec.id}`, pipeline: null, cc: rec });
        continue;
      }
      ccByRef.set(matterRef, rec);
    }

    const pipelineMatterRefs = new Set();
    const pipelineRows = pipelineItems.map((item) => {
      const matterRef = normRef(item.matter_reference);
      if (!matterRef) {
        // No matter ref - keep as its own standalone row so nothing is hidden.
        return { key: `__pipeline__${item.id}`, pipeline: item, cc: null };
      }

      pipelineMatterRefs.add(matterRef);
      const creditControlId = String(item.credit_control_id || "").trim();
      let linkedCC = null;
      if (creditControlId === "__unlinked__") {
        // Explicit day-to-day unlink: never fall back to a same-matter CC.
        linkedCC = null;
      } else if (creditControlId) {
        // A real stored link is authoritative, including when the target
        // record has subsequently been removed or is not in this response.
        linkedCC = ccById.get(creditControlId) || null;
      } else {
        // Legacy BM-H144 behaviour for requests that have never had a
        // per-request linkage stored.
        linkedCC = ccByRef.get(matterRef) || null;
      }
      return {
        key: item.id,
        pipeline: item,
        cc: linkedCC,
      };
    });

    const ccOnlyRows = Array.from(ccByRef.entries())
      .filter(([matterRef]) => !pipelineMatterRefs.has(matterRef))
      .map(([, cc]) => ({
        key: `__cc__${cc.id}`,
        pipeline: null,
        cc,
      }));

    return [...pipelineRows, ...standaloneCCRows, ...ccOnlyRows];
  }, [pipelineItems, ccRecords]);

  // Item 21-patch (#189): free-text search is resolved server-side via
  // /api/smart-search (search_resolver.resolve) instead of a client-side
  // substring match — "one resolver, everywhere" per
  // backend/hub/SEARCH_RESOLVER.md. /api/smart-search has no invoice-tracker
  // scope param, so the card filter (draft/awaiting/outstanding/recovery)
  // stays client-side on top of the resolved matter set.
  const { matchedRefs, matchedClientNames } = useSmartSearchResolver(search);

  // ── Apply search + card filter ──────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const searching = search.trim().length >= 2;
    return rows.filter(({ pipeline, cc }) => {
      // Card filters.
      if (cardFilter === "draft_needed" && !(pipeline && getPipelineStatus(pipeline) === "")) return false;
      if (cardFilter === "awaiting" && !(pipeline && getPipelineStatus(pipeline) === "notified")) return false;
      if (cardFilter === "outstanding" && !(cc && (cc.total_outstanding || 0) > 0)) return false;
      if (cardFilter === "recovery" && !(cc && RECOVERY_STAGES.has(cc.escalation_stage))) return false;

      // Search — resolved via /api/smart-search (see useSmartSearchResolver).
      // Empty search box: show full list as before (untouched behaviour).
      if (searching) {
        const ref = (pipeline?.matter_reference || cc?.matter_ref || "").toUpperCase();
        const clientLower = (pipeline?.client_name || cc?.client_name || "").toLowerCase();
        if (!matchedRefs.has(ref) && !matchedClientNames.has(clientLower)) return false;
      }
      return true;
    });
  }, [rows, cardFilter, search, matchedRefs, matchedClientNames]);

  const loading = pipelineLoading || ccLoading;

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy to-navy">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-2 -ml-2 text-xs text-slate-400">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Dashboard
        </Button>
        {/* 27 Oct 2026 system-wide refresh: header band. */}
        <PageHero icon={Receipt} title="Invoice Tracker" testId="invoice-tracker-hero"
          subtitle="Every invoice's full lifecycle, draft to recovery, in one view"
          tile="linear-gradient(135deg, #0ea5e9, #6366f1)">
          {canWrite && (
            <Button onClick={() => setShowCreate(true)} size="sm">
              <Plus className="w-4 h-4 mr-1" /> New CC Record
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchAll} className="text-gray-300 border-white/15" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </PageHero>
        {(pipelineError || ccError) && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-status-red/40 bg-status-red/10 px-3 py-2 text-sm text-status-red" role="alert">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Some data could not be loaded.</p>
              {pipelineError && <p className="text-xs text-status-red/80">Pipeline: {pipelineError}</p>}
              {ccError && <p className="text-xs text-status-red/80">Credit control: {ccError}</p>}
            </div>
          </div>
        )}

        <SummaryCards rows={rows} ccSummary={ccSummary} activeFilter={cardFilter} onFilter={setCardFilter} />

        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-faint" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search matter, client, fee earner..."
            className="w-full bg-navy-card border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-[#5A6A6A] focus:outline-none focus:ring-2 focus:ring-teal/40" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-teal animate-spin" /></div>
        ) : filteredRows.length === 0 ? (
          <div className="text-center py-16">
            <Receipt className="w-10 h-10 text-txt-faint mx-auto mb-3" />
            <p className="text-txt-muted">No invoices found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRows.map((row, idx) => (
              <LifecycleRow
                key={row.pipeline?.id || row.cc?.id || row.key || idx}
                row={row}
                navigate={navigate}
                canWrite={canWrite}
                onAllocated={fetchPipeline}
                onUnlinkRequested={setUnlinkingPipeline}
              />
            ))}
          </div>
        )}
      </div>

      <CreateCCModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={fetchCC} />
      <UnlinkCCModal
        pipeline={unlinkingPipeline}
        onClose={() => setUnlinkingPipeline(null)}
        onUnlinked={fetchAll}
      />
    </div>
  );
}
